<script setup lang="ts">
// The "Used N tools" fold - the SINGLE tool-call renderer shared by Studio, the
// quick-ask popover, and the Home thread. Lifted verbatim from StudioView so
// every surface shows tool calls identically (Claude-CLI flavour: collapsible
// fold -> per-tool bullet + Name(args) / L-elbow + result, click to expand for
// full args/result JSON + Copy). Don't fork this; feed it ToolStep[].
import AppIcon from '@/components/AppIcon.vue';
import { ref } from 'vue';
import { useI18n } from '@/lib/i18n';
import { useToastStore } from '@/stores/toasts';
import { Fold, Tooltip } from '@hq/chat-ui';
import type { ToolStep } from '@/types/tool';

defineProps<{ tools: ToolStep[] }>();

const { t } = useI18n();
const toast = useToastStore();

function prettyToolName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
// Hide noise args: empty / {} / [] / null render nothing in the summary.
function showArgs(summary?: string): boolean {
  if (!summary) return false;
  const s = summary.replace(/\s+/g, '');
  return s !== '' && s !== '{}' && s !== '[]' && s !== 'null';
}
function toCopyableText(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value, null, 2); }
  catch { return String(value); }
}

// One open-tool's copy state. The component is per-bubble, so the tool index is
// a sufficient key (no bubble index needed, unlike the old inline version).
const copiedIdx = ref<number | null>(null);
async function copyPayload(idx: number, text: string): Promise<void> {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    copiedIdx.value = idx;
    window.setTimeout(() => { if (copiedIdx.value === idx) copiedIdx.value = null; }, 1600);
  } catch {
    // Clipboard may be unavailable on insecure origins; nudge the user to
    // select + copy from the <pre> instead of failing silently.
    toast.error('Clipboard unavailable - select the text manually.');
  }
}
</script>

<template>
  <Fold v-if="tools && tools.length">
    <template #summary>
      <span>{{
        tools.length === 1
          ? t('studio.tools.used_one')
          : t('studio.tools.used_other', { count: tools.length })
      }}</span>
    </template>
    <ul class="bubble__tools" role="list">
      <li v-for="(tc, j) in tools" :key="j" class="tool">
        <details class="tool-detail" :class="{ 'tool-detail--error': tc.isError }">
          <summary class="tool-detail__summary">
            <div class="tool-detail__call">
              <AppIcon name="dot" :size="18" class="tool-detail__bullet" />
              <span class="tool-detail__name">{{ prettyToolName(tc.name) }}</span>
              <span v-if="showArgs(tc.argsSummary)" class="tool-detail__args">({{ tc.argsSummary }})</span>
            </div>
            <div v-if="tc.result" class="tool-detail__result">
              <AppIcon name="arrow-bend-down-right" :size="13" class="tool-detail__elbow" />
              <span class="tool-detail__result-text">{{ tc.result }}</span>
            </div>
          </summary>
          <div class="tool-detail__body">
            <Tooltip :label="t('studio.tools.copy')">
              <button
                type="button"
                class="tool-detail__copy"
                @click.stop.prevent="copyPayload(j, toCopyableText(tc.outputFull ?? tc.argsFull ?? tc.result ?? tc.argsSummary))"
              >
                <AppIcon name="check" v-if="copiedIdx === j" :size="13" />
                <AppIcon name="copy" v-else :size="13" />
                <span>{{ copiedIdx === j ? t('studio.tools.copied') : t('studio.tools.copy') }}</span>
              </button>
            </Tooltip>
            <template v-if="showArgs(tc.argsSummary) || (tc.argsFull !== undefined && showArgs(JSON.stringify(tc.argsFull)))">
              <div class="tool-detail__label">{{ t('studio.tools.label_args') }}</div>
              <pre class="tool-detail__code"><code>{{ toCopyableText(tc.argsFull ?? tc.argsSummary) }}</code></pre>
            </template>
            <template v-if="tc.outputFull || tc.result">
              <div class="tool-detail__label">{{ t('studio.tools.label_result') }}</div>
              <pre class="tool-detail__code"><code>{{ tc.outputFull ?? tc.result }}</code></pre>
            </template>
          </div>
        </details>
      </li>
    </ul>
  </Fold>
</template>

<style scoped lang="scss">
// The fold chrome (chevron + collapsible summary) lives in the shared Fold
// component; here we only style the per-tool list body.
.bubble__tools {
  margin: var(--space-2) 0 0;
  padding: 0 0 0 var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  list-style: none;
}
.tool {
  font-size: var(--text-xs);
  color: var(--ink-muted);
}

// Per-tool details: Claude-CLI flavour. Two visible rows in the summary (call
// + result), click to expand for full JSON + Copy. Native <summary> marker
// hidden - the Phosphor Dot icon is the visual bullet.
.tool-detail {
  > summary {
    list-style: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;
    &::-webkit-details-marker { display: none; }
    &:hover { .tool-detail__name { color: var(--accent-default); } }
  }
}
.tool-detail__call {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  line-height: 1.3;
}
.tool-detail__bullet {
  color: var(--ink-primary);
  flex: 0 0 auto;
}
.tool-detail__name {
  color: var(--ink-primary);
  font-weight: 500;
  transition: color 120ms ease;
}
.tool-detail__args {
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.tool-detail__result {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding-left: calc(var(--space-2) + 18px);
  color: var(--ink-muted);
  line-height: 1.3;
}
.tool-detail__elbow {
  color: var(--ink-muted);
  flex: 0 0 auto;
  margin-top: 2px;
}
.tool-detail__result-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.tool-detail--error {
  .tool-detail__bullet, .tool-detail__name, .tool-detail__result-text {
    color: var(--status-danger-text);
  }
}

// Expanded body: copy button + labelled code blocks for args and result.
.tool-detail__body {
  margin-top: var(--space-2);
  padding-left: calc(var(--space-2) + 18px);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.tool-detail__copy {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  background: var(--surface-panel);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-input);
  padding: 2px 6px;
  cursor: pointer;
  color: var(--ink-muted);
  font-size: var(--text-xs);
  transition: color 120ms ease, border-color 120ms ease;
  &:hover { color: var(--ink-primary); border-color: var(--ink-muted); }
}
.tool-detail__label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 10px;
  color: var(--ink-muted);
  margin-top: var(--space-1);
}
.tool-detail__code {
  margin: 0;
  padding: var(--space-2);
  background: var(--surface-panel);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-input);
  max-height: 24em;
  overflow: auto;
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, monospace);
  font-size: var(--text-xs);
  line-height: 1.45;
  white-space: pre;
  color: var(--ink-primary);
}
</style>
